import { Container, InstanceLifetime } from '../engine/Container';
import { DebugConsole } from '../engine/DebugConsole';
import { EventStreamProvider } from '../engine/EventStreamProvider';
import { FileResourceCache } from '../engine/FileResourceCache';
import { IConcreteResourceCache } from '../engine/IResourceCache';
import { Platform } from '../engine/Platform';
import { PlatformSettings } from '../engine/PlatformSettings';
import { Preloader } from '../engine/Preloader';
import { RESIZE_HANDLER_TOKEN, ResizeHandler } from '../engine/ResizeHandler';
import { StopWatch } from '../engine/StopWatch';
import { Timer } from '../engine/Timer';
import { Application } from './Application';
import { ApplicationResourceCache } from './ApplicationResourceCache';
import { ASSETS_URL_FORMATTER_TOKEN, IAssetsUrlFormatter } from '../helpers/assets-url.formatter';
import { MAX_DELTA_TIME } from '../constants/fps';

export class ApplicationRender {
  readonly ContextNotRestoredTimeout = 3;

  private readonly _window: Window;

  private _application: Application;
  private readonly _platform: Platform;
  private _preloader: Preloader | null = null;
  private applicationResourceCache: ApplicationResourceCache;

  private _time: number | null = null;

  private _framesCount = 0;
  private _fps = 0;
  private _fpsTime = 0.0;

  private _totalUpdate = 0.0;
  private _averageUpdate = 0;

  private _totalDraw = 0.0;
  private _averageDraw = 0;

  private _animationFrame: number;

  private _contextRestoreTimer: Timer | null = null;

  private readonly _stopWatch = new StopWatch();

  constructor(
    window: Window,
    private canvas: HTMLCanvasElement,
    settings: PlatformSettings = new PlatformSettings(),
    container: Container,
    resourceCache: IConcreteResourceCache = new FileResourceCache()
  ) {
    this._preloader = settings.preloader;
    this._window = window;

    this._platform = new Platform(canvas, settings);

    // resizer
    const resizer = new ResizeHandler(this._platform.view, settings.resizeMode, settings.minBackbufferWidth);
    container.registerValue(RESIZE_HANDLER_TOKEN, resizer, {
      lifetime: InstanceLifetime.Singleton,
    });
    resizer.top = settings.top;
    resizer.touchListener();

    this.applicationResourceCache = new ApplicationResourceCache(resourceCache, () =>
      container.resolve<IAssetsUrlFormatter>(ASSETS_URL_FORMATTER_TOKEN, {
        optional: true,
      })
    );

    this.applicationResourceCache.beforeLoad.listen(() => {
      resizer.debouncedResize();
    });
  }

  public get platform(): Platform {
    return this._platform;
  }

  public async buildApp() {
    await this.applicationResourceCache.registerPools(this._platform);

    this.handleWebGLContext();

    return {
      platform: this._platform,
      resourceCache: this.applicationResourceCache.resourceCache,
    };
  }

  public registerApplication(application: Application): void {
    this._application = application;
  }

  private handleWebGLContext() {
    this._animationFrame = this._window.requestAnimationFrame((time: number) => this._frame(time));

    document.onvisibilitychange = () => this._onVisibilityChange();

    EventStreamProvider.subscribeElementTyped<WebGLContextEvent>(
      this.canvas,
      'webglcontextlost',
      this._onContextLost,
      WebGLContextEvent
    );
    EventStreamProvider.subscribeElementTyped<WebGLContextEvent>(
      this.canvas,
      'webglcontextrestored',
      this._onContextReady,
      WebGLContextEvent
    );
  }

  private _frame(highResTime: number): void {
    if (this._animationFrame < 0) {
      return;
    }
    this._animationFrame = this._window.requestAnimationFrame(this._frame.bind(this));

    if (this._time === null) {
      this._time = highResTime;
    } else if (this._preloader) {
      // second frame
      this._preloader.hide();
      this._preloader = null;
    }
    if (this._time > highResTime) this._time = highResTime;

    const deltaTime = highResTime - this._time;

    if (deltaTime > 0) {
      this._time = highResTime;
      let dt = deltaTime * 0.001;
      if (dt > MAX_DELTA_TIME) {
        dt = MAX_DELTA_TIME;
      }
      this._doFrame(dt);
    }

    if (highResTime - this._fpsTime > 1000) {
      this._fps = this._framesCount;
      this._framesCount = 0;
      this._fpsTime = highResTime;

      if (this._fps > 0) {
        this._averageUpdate = Math.floor(this._totalUpdate / this._fps);
        this._averageDraw = Math.floor(this._totalDraw / this._fps);
      }

      this._totalUpdate = 0.0;
      this._totalDraw = 0.0;
    }
  }

  private _doFrame(dt: number): void {
    this._framesCount++;

    const app = this._application;

    this._platform.beginFrame();

    this._stopWatch.start();
    app.update(dt);
    this._stopWatch.stop();
    let t = this._stopWatch.elapsedMicroseconds;
    DebugConsole.Print(`update: ${t} [${this._averageUpdate}]`);
    this._totalUpdate += t;

    this._stopWatch.start();
    app.draw();
    this._stopWatch.stop();
    t = this._stopWatch.elapsedMicroseconds;
    this._totalDraw += t;
    DebugConsole.Print(`draw: ${t} [${this._averageDraw}]`);
    DebugConsole.Print(`fps: ${this._fps}`);

    DebugConsole.Draw();

    this._platform.endFrame();
  }

  private _onVisibilityChange(): void {
    this._application.activate(!document.hidden);
    this._time = null;
  }

  private _onContextLost(event?: WebGLContextEvent): void {
    event!.preventDefault();

    this._window.cancelAnimationFrame(this._animationFrame);
    this._animationFrame = -1;

    this._application.contextLost();
    this._platform.contextLost();

    this._contextRestoreTimer = new Timer(this.ContextNotRestoredTimeout * 1000, () =>
      this._handleContextNotRestored()
    );
  }

  private _onContextReady(_event?: WebGLContextEvent): void {
    this._contextRestoreTimer?.cancel();
    this._contextRestoreTimer = null;

    this._platform.contextReady();
    const ready = this._application.contextReady();

    ready.then(() => {
      this._animationFrame = this._window.requestAnimationFrame(this._frame.bind(this));
    });
  }

  private _handleContextNotRestored(): void {
    this._application.handleContextNotRestored();
  }
}
